import { logger } from "@/utils/logger";

import React, { useRef } from 'react';
import FormFramework, { FormFrameworkRef } from '../FormFramework';
import NewConversationDialog from '../NewConversationDialog';
import TradeBanner from '../forms/TradeBanner';
import ChatHeader from './ChatHeader';
import ChatMessages from './ChatMessages';
import ChatInput from './ChatInput';
import { useTradeEstimate } from '@/hooks/useTradeEstimate';
import { useFormContext } from '@/contexts/FormContext';
import { useFormAutoCollapse } from '@/hooks/useFormAutoCollapse';
import { FormAutoCollapseProvider, useFormAutoCollapseControl } from '@/contexts/FormAutoCollapseContext';
import { DealerManager } from '@/utils/dealerManager';
import { useChatMessages } from '@/hooks/useChatMessages';
import { useLanguage } from '@/contexts/LanguageContext';
import { ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';

interface ChatLayoutMobileProps {
  // Chat state
  messages: any[];
  isLoading: boolean;
  isSearching?: boolean;
  isRestoring?: boolean;
  chatId: string;
  sessionId: string;
  dealerId: string;
  waitingVideoData?: {
    Waiting_Youtube_Video_url: string;
    Waiting_Message: string;
  } | null;
  // Note: Message classification is now handled server-side
  streamingIntro?: {
    displayedIntro: string;
    isTyping: boolean;
    isComplete: boolean;
    isStreaming: boolean;
    isEnabled: boolean;
  };
  
  // Form state
  activeForm: any;
  isOfferHighlighted: boolean;
  formData: any;
  updateFormData: (data: any) => void;
  closeForm: (force?: boolean) => void;
  
  // Input state
  input: string;
  setInput: (input: string) => void;
  
  // Dialog state
  showNewConversationDialog: boolean;
  setShowNewConversationDialog: (show: boolean) => void;
  
  // Handlers
  onNewConversation: () => void;
  onCopyConversation: (messagesContainerRef?: React.RefObject<HTMLElement>) => void;
  onCollapse: () => void;
  onFormSubmit: (data: any) => void;
  onSendMessage: (message?: string) => void;
  onConfirmNewConversation: () => void;
  onRetryLastRequest?: () => Promise<void>;
}

const ChatLayoutMobile: React.FC<ChatLayoutMobileProps> = ({
  messages,
  isLoading,
  isSearching = false,
  isRestoring = false,
  chatId,
  sessionId,
  dealerId,
  waitingVideoData,
  streamingIntro,
  activeForm,
  isOfferHighlighted,
  formData,
  updateFormData,
  closeForm,
  input,
  setInput,
  showNewConversationDialog,
  setShowNewConversationDialog,
  onNewConversation,
  onCopyConversation,
  onCollapse,
  onFormSubmit,
  onSendMessage,
  onConfirmNewConversation,
  onRetryLastRequest,
}) => {
  const {
    isTradeEstimateEnabled,
    isTradeEstimateVisible,
    hideTradeEstimate,
    showTradeEstimate,
  } = useTradeEstimate();

  // Hide trade estimate when other forms are active
  React.useEffect(() => {
    if (activeForm) {
      hideTradeEstimate();
    } else if (!activeForm && isTradeEstimateEnabled) {
      showTradeEstimate();
    }
  }, [activeForm, hideTradeEstimate, showTradeEstimate, isTradeEstimateEnabled]);

  const { setFormRef, openingHours } = useFormContext();
  const isOnHomePage = true; // Widget is always treated as "home page"
  const formRef = useRef<FormFrameworkRef>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const [isFormCollapsed, setIsFormCollapsed] = React.useState(false);
  const { addAIMessage } = useChatMessages();
  const { t } = useLanguage();

  // Handler for form expansion from prompt menu
  const handleFormExpand = React.useCallback((formType: string) => {
    logger.log('🔧 Mobile Layout: Handling form expand for:', formType);
    // Clear input
    setInput('');
    
    // Add AI message for appointment forms
    if (formType === 'Appointment' || formType === 'Service') {
      const message = formType === 'Appointment' 
        ? t('chat.appointment.sales.message')
        : t('chat.appointment.service.message');
      
      setTimeout(async () => {
        await addAIMessage(message);
      }, 200);
    }
    
    // DO NOT expand form immediately - let useFormAutoCollapse handle it
    // Form will start collapsed and expand when user scrolls to bottom
    // This allows users to read the AI response before seeing the form
    
  }, [setInput, addAIMessage, t]);

  // Close all forms when OpenAI is loading (except Service/Appointment which should persist)
  //
  // See the matching comment in ChatLayoutDesktop.tsx: `closeForm` and
  // `hideTradeEstimate` change identity every render, so listing them as
  // dependencies re-ran this effect after every commit and `closeForm()`'s
  // always-new state object turned that into an unbounded render loop.
  const closeFormRef = React.useRef(closeForm);
  closeFormRef.current = closeForm;
  const hideTradeEstimateRef = React.useRef(hideTradeEstimate);
  hideTradeEstimateRef.current = hideTradeEstimate;

  React.useEffect(() => {
    if (!isLoading) return;
    if (activeForm !== 'Service' && activeForm !== 'Appointment') {
      if (activeForm) closeFormRef.current();
      hideTradeEstimateRef.current();
    }
  }, [isLoading, activeForm]);

  // Enable/disable form auto-collapse
  const AUTO_COLLAPSE_ENABLED = true;
  
  // Get auto-collapse control
  const { isTemporaryDisabled } = useFormAutoCollapseControl();

  // Always call useFormAutoCollapse to avoid hooks count mismatch
  useFormAutoCollapse({
    enabled: AUTO_COLLAPSE_ENABLED && !!activeForm,
    formRef,
    scrollAreaRef: chatScrollRef,
    temporaryDisabled: isTemporaryDisabled
  });

  // Set the form ref in context so VehicleCard can access it
  React.useEffect(() => {
    setFormRef(formRef);
  }, [setFormRef]);

  // Collapse form when streaming starts
  React.useEffect(() => {
    if (streamingIntro?.isStreaming && formRef.current) {
      logger.log('🔧 Mobile Layout: Collapsing form due to streaming start');
      formRef.current.collapse();
    }
  }, [streamingIntro?.isStreaming]);

  const handleCollapseForm = () => {
    if (formRef.current) {
      formRef.current.collapse();
    }
  };

  return (
    <>
      <div className="d2cbot-chat-container fixed left-1/2 -translate-x-1/2 z-30 bg-white flex flex-col animate-scale-in p-0 m-0 overflow-hidden rounded-t-2xl" style={{ height: '80vh', width: '90vw', bottom: '5vh', top: 'auto', boxShadow: '0 -4px 24px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06)' }}>
        <ChatHeader
          onNewConversation={onNewConversation}
          onCopyConversation={onCopyConversation}
          onCollapse={onCollapse}
          onSendMessage={onSendMessage}
          messagesContainerRef={chatScrollRef}
        />

        {/* Trade Estimate Form - appears right under header when enabled */}
        {isTradeEstimateEnabled && isTradeEstimateVisible && DealerManager.isTradeBannerEnabled() && (
          <TradeBanner
            onClose={hideTradeEstimate}
            onSubmit={onFormSubmit}
            activeForm={activeForm}
            isOnHomePage={isOnHomePage}
            isFormCollapsed={isFormCollapsed}
          />
        )}

        <ChatMessages
          ref={chatScrollRef}
          messages={messages}
          isLoading={isLoading}
          isSearching={isSearching}
          isRestoring={isRestoring}
          sessionId={sessionId}
          onSendMessage={onSendMessage}
          inputValue={input}
          setInputValue={setInput}
          onFormExpand={handleFormExpand}
          waitingVideoData={waitingVideoData}
          onRetryLastRequest={onRetryLastRequest}
          onCopyConversation={() => onCopyConversation(chatScrollRef)}
          streamingIntro={streamingIntro}
        />

        {/* Form Framework - pinned between messages and input */}
        {/* Forms start collapsed and expand when user scrolls to bottom of messages */}
        {activeForm && (
        <FormFramework
          ref={formRef}
          formType={activeForm}
          formData={formData}
          onUpdateData={updateFormData}
          onClose={() => closeForm(true)}
          onSubmit={onFormSubmit}
          isOfferHighlighted={isOfferHighlighted}
          onCollapse={setIsFormCollapsed}
          openingHours={openingHours}
          initialCollapsed={true}
        />
        )}

        <ChatInput
          inputValue={input}
          setInputValue={setInput}
          onSendMessage={onSendMessage}
          onCollapseForm={handleCollapseForm}
          disabled={isLoading}
          isWelcomeScreen={messages.length === 0}
          sessionId={sessionId}
          isFormCollapsed={isFormCollapsed}
          waitingVideoData={waitingVideoData}
          isLoading={isLoading}
        />
        
        {/* Footer with copyright and collapse button */}
        <div className="flex items-center justify-between px-4 py-[5px]">
          <span className="text-[11px] text-gray-400">
            {t('footer.disclaimer')}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCollapse}
            className="chatbot-close-btn flex items-center gap-1 text-gray-500 hover:text-gray-700 md:hidden"
            aria-label={t('chat.closeChat')}
          >
            <ChevronDown className="h-4 w-4" />
            <span className="text-xs">{t('chat.closeChat')}</span>
          </Button>
        </div>
      </div>

      <NewConversationDialog
        open={showNewConversationDialog}
        onOpenChange={setShowNewConversationDialog}
        onConfirm={onConfirmNewConversation}
      />
    </>
  );
};

const ChatLayoutMobileWithProvider: React.FC<ChatLayoutMobileProps> = (props) => {
  return (
    <FormAutoCollapseProvider>
      <ChatLayoutMobile {...props} />
    </FormAutoCollapseProvider>
  );
};

export default ChatLayoutMobileWithProvider;
